import { type IntranetCategory } from '@gb-ticket/shared-types';
import { ArrowLeftIcon, EyeIcon, Loader2Icon, PlusIcon, UsersIcon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import {
  intranetPanelClassName,
  intranetTableFrameClassName,
} from '@/products/intranet/features/intranet/intranet-panel';
import {
  useCreateIntranetCategory,
  useDeleteIntranetCategory,
  useIntranetCategories,
  useIntranetCategoryPosterRoles,
  useUpdateIntranetCategory,
} from '@/products/intranet/features/intranet/use-intranet';
import {
  INTRANET_EDITORIAL_PATH,
  intranetCategorySettingsPath,
} from '@/products/intranet/intranet-paths';

export function CategoriesPage() {
  usePageTitle('Channels');
  const categories = useIntranetCategories();
  const create = useCreateIntranetCategory();
  const update = useUpdateIntranetCategory();
  const remove = useDeleteIntranetCategory();
  const roles = useIntranetCategoryPosterRoles();
  const [name, setName] = useState('');
  const [managerRoleId, setManagerRoleId] = useState('');
  const [pendingDelete, setPendingDelete] = useState<IntranetCategory | null>(null);
  const [access, setAccess] = useState<{
    category: IntranetCategory;
    kind: 'posters' | 'readers';
  } | null>(null);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const items = categories.data?.items ?? [];

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error('Bitte einen Namen mit mindestens 2 Zeichen eingeben.');
      return;
    }
    if (!managerRoleId) {
      toast.error('Bitte eine verwaltende Rolle wählen.');
      return;
    }
    try {
      await create.mutateAsync({
        name: trimmed,
        managerRoleId,
        sortOrder: (items.length + 1) * 10,
      });
      setName('');
      setManagerRoleId('');
      toast.success('Channel angelegt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Anlegen fehlgeschlagen.');
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success('Channel gelöscht.');
      setPendingDelete(null);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.');
    }
  }

  async function saveAccess() {
    if (!access) {
      return;
    }
    const body =
      access.kind === 'posters' ? { posterRoleIds: roleIds } : { readerRoleIds: roleIds };
    try {
      await update.mutateAsync({ id: access.category.id, body });
      toast.success(
        access.kind === 'posters' ? 'Channel-Autoren gespeichert.' : 'Leser gespeichert.',
      );
      setAccess(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : access.kind === 'posters'
            ? 'Channel-Autoren speichern fehlgeschlagen.'
            : 'Leser speichern fehlgeschlagen.',
      );
    }
  }

  function openAccess(category: IntranetCategory, kind: 'posters' | 'readers') {
    setRoleIds(
      kind === 'posters' ? (category.posterRoleIds ?? []) : (category.readerRoleIds ?? []),
    );
    setAccess({ category, kind });
  }

  return (
    <section className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to={INTRANET_EDITORIAL_PATH}>
          <ArrowLeftIcon aria-hidden />
          Zur Redaktion
        </Link>
      </Button>
      <PlatformPageHeader
        title="Channels"
        description="Ordnen Beiträge thematisch und legen fest, welche Gruppen den Channel sehen und darin schreiben dürfen."
      />

      <div className={`${intranetPanelClassName} text-sm text-muted-foreground`}>
        <p>
          Ohne ausgewählte Gruppe dürfen alle Personen mit globalem Intranet-Schreibrecht im Channel
          schreiben.
        </p>
        <p>
          Sobald Gruppen gewählt sind, muss auch die Intranet-Redaktion eingetragen sein. IT-Admins
          mit Verwaltungsrecht dürfen unabhängig von dieser Auswahl schreiben.
        </p>
        <p>
          Geschlossen und ohne Leser-Rollen und ohne direkte Mitglieder sehen alle Intranet-Nutzer
          den Channel. Sonst sehen ihn Leser-Rollen, direkte Mitglieder, Autoren-Rollen, die
          verwaltende Rolle, Personen mit intranet.content:manage und Personen mit
          intranet.categories:manage. Offen hebt die Regel auf, dass ohne Mitgliedschaft alle den
          Channel sehen.
        </p>
      </div>

      <form className={`${intranetPanelClassName} space-y-4`} onSubmit={onSubmit}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <Label htmlFor="intranet-category-name">Neuer Channel</Label>
            <Input
              id="intranet-category-name"
              value={name}
              maxLength={80}
              placeholder="z. B. HR & Personal"
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="flex-1 space-y-2">
            <Label htmlFor="intranet-category-manager">Verwaltende Rolle</Label>
            <select
              id="intranet-category-manager"
              className="flex h-11 w-full rounded-md border bg-background px-3 text-sm"
              value={managerRoleId}
              onChange={(event) => setManagerRoleId(event.target.value)}
              required
            >
              <option value="">Bitte wählen</option>
              {(roles.data?.items ?? []).map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" disabled={create.isPending}>
            <PlusIcon aria-hidden />
            Anlegen
          </Button>
        </div>
      </form>

      {categories.isPending ? (
        <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Channels werden geladen …
        </p>
      ) : items.length === 0 ? (
        <p className={`${intranetPanelClassName} border-dashed px-4 py-12 text-center text-sm`}>
          Noch keine Channels.
        </p>
      ) : (
        <ul className={`${intranetTableFrameClassName} divide-y`}>
          {items.map((category) => (
            <li
              key={category.id}
              className="flex min-h-11 flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <span className="text-sm font-medium">{category.name}</span>
                <p className="text-xs text-muted-foreground">
                  {(category.readerRoleIds?.length ?? 0) === 0
                    ? 'Alle sehen den Channel'
                    : `${category.readerRoleIds?.length ?? 0} Lesergruppe(n)`}
                  {' · '}
                  {(category.posterRoleIds?.length ?? 0) === 0
                    ? 'Alle mit globalem Schreibrecht'
                    : `${category.posterRoleIds?.length ?? 0} Autorengruppe(n)`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button asChild variant="outline" size="sm">
                  <Link to={intranetCategorySettingsPath(category.slug)}>Einstellungen</Link>
                </Button>
                <Button variant="outline" size="sm" onClick={() => openAccess(category, 'readers')}>
                  <EyeIcon aria-hidden />
                  Leser
                </Button>
                <Button variant="outline" size="sm" onClick={() => openAccess(category, 'posters')}>
                  <UsersIcon aria-hidden />
                  Autoren
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => setPendingDelete(category)}
                >
                  Löschen
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        title="Channel löschen?"
        description={
          pendingDelete
            ? `„${pendingDelete.name}“ wird entfernt. Beiträge darin bleiben erhalten, nur ohne Channel.`
            : ''
        }
        pending={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />

      <Dialog
        open={access !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAccess(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{access?.kind === 'readers' ? 'Leser' : 'Channel-Autoren'}</DialogTitle>
            <DialogDescription>
              {access?.kind === 'readers'
                ? `Gruppen auswählen, die „${access.category.name}“ und ihre Beiträge sehen dürfen.`
                : access
                  ? `Gruppen auswählen, die in „${access.category.name}“ Nachrichten schreiben dürfen.`
                  : null}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {roles.isPending ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
                Gruppen werden geladen …
              </p>
            ) : null}
            {(roles.data?.items ?? []).map((role) => (
              <label
                key={role.id}
                className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-sm"
              >
                <Checkbox
                  checked={roleIds.includes(role.id)}
                  onCheckedChange={(checked) =>
                    setRoleIds((current) =>
                      checked === true
                        ? [...new Set([...current, role.id])]
                        : current.filter((id) => id !== role.id),
                    )
                  }
                />
                <span>
                  <span className="font-medium">{role.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {role.isSystem ? 'Systemrolle' : 'Eigene Gruppe'}
                  </span>
                </span>
              </label>
            ))}
            {!roles.isPending && (roles.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">Keine Berechtigungsgruppen verfügbar.</p>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            {access?.kind === 'readers'
              ? 'Geschlossen und ohne Leser-Rollen und ohne direkte Mitglieder: alle Intranet-Nutzer sehen den Channel. Sonst Leser-Rollen, direkte Mitglieder, Autoren-Rollen, die verwaltende Rolle, intranet.content:manage und intranet.categories:manage. Offen hebt auf, dass ohne Mitgliedschaft alle ihn sehen.'
              : 'Leere Auswahl: alle mit globalem Schreibrecht. IT-Admins mit Verwaltungsrecht sind immer zugelassen.'}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAccess(null)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              disabled={update.isPending || roles.isPending}
              onClick={() => void saveAccess()}
            >
              {update.isPending ? 'Wird gespeichert …' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
